import React, { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { LIVE_HOME_PATH } from "./constants";
import { useStagingPreview } from "./context";
import { toast } from "@/components/ui/use-toast";

interface StagingRedirectGuardProps {
  /** The real page component to render when NOT in staging preview mode. */
  children: React.ReactNode;
  /**
   * Optional override for the user-facing toast message. Defaults to the
   * generic "action disabled in preview" string.
   */
  disabledMessage?: string;
}

const DEFAULT_MESSAGE = "This action is disabled in staging preview.";

/**
 * Wrapper that blocks customer-critical routes (cart / checkout / payment / orders /
 * e-gift / iris) while the browser session is in staging preview mode. Renders the
 * wrapped children unchanged in live mode.
 *
 * <p>In staging mode it:
 * <ol>
 *   <li>Triggers a {@code toast()} explaining the action is disabled (once — guarded
 *       with a ref so StrictMode double-renders don't double-fire).
 *   <li>Issues a {@code <Navigate to="/" replace />} to the storefront home.
 * </ol>
 *
 * <p>The server-side guard in {@code grm-bff} will 404 any customer-critical API call
 * anyway; this component is the UX layer that prevents the customer from reaching
 * the broken page in the first place.
 */
export function StagingRedirectGuard({ children, disabledMessage }: StagingRedirectGuardProps) {
  const { cycleId } = useStagingPreview();
  const toastedRef = useRef(false);

  useEffect(() => {
    if (cycleId !== null && !toastedRef.current) {
      toastedRef.current = true;
      toast({
        title: "Preview mode",
        description: disabledMessage ?? DEFAULT_MESSAGE,
        variant: "destructive",
      });
    }
  }, [cycleId, disabledMessage]);

  if (cycleId !== null) {
    return <Navigate to={LIVE_HOME_PATH} replace />;
  }

  return <>{children}</>;
}
